// `grid agent` — write CloudGrid agent-onboarding files into the cwd.
// Offline, no auth, no network. Plan -> consent -> apply. See spec
// docs/superpowers/specs/2026-07-05-cloudgrid-agent-command-design.md.

import { Command } from 'commander';
import { V2ApiError } from '../api/v2-errors.js';
import { handleV2Error } from '../handle-error.js';
import { isStructuredOutput } from '../services/output-format.js';
import inquirer from 'inquirer';
import { readFileSync, writeFileSync, lstatSync, readlinkSync } from 'fs';
import { join, dirname, isAbsolute, resolve } from 'path';
import { CloudGridError } from '../utils.js';
import { Output } from '../output.js';
import { addOutputOption } from '../services/output-format.js';
import {
  AGENT_BOOTSTRAP_BLOCK,
  CLOUDGRID_BLOCK_START,
  CLOUDGRID_BLOCK_END,
  mergeManagedBlock,
  ensureClaudeImport,
  countBlockStarts,
  CorruptedBlockError,
  type MergeStatus,
} from '../lib/agent-files.js';

export interface AgentOpts {
  yes?: boolean;
  dryRun?: boolean;
  json?: boolean;
  output?: string;
}
export interface AgentDeps {
  cwd?: string;
  isTTY?: boolean;
  confirm?: (message: string) => Promise<boolean>;
}

interface PlanItem {
  label: string;
  path: string;
  status: MergeStatus;
  content: string;
  note?: string;
  warn?: string;
}

function readOrNull(path: string): string | null {
  try {
    return readFileSync(path, 'utf8');
  } catch {
    return null;
  }
}

function isSymlinkTo(path: string, targetPath: string): boolean {
  try {
    if (!lstatSync(path).isSymbolicLink()) return false;
    const link = readlinkSync(path);
    const resolved = isAbsolute(link) ? link : resolve(dirname(path), link);
    return resolve(resolved) === resolve(targetPath);
  } catch {
    return false;
  }
}

async function defaultConfirm(message: string): Promise<boolean> {
  const { ok } = await inquirer.prompt([{ type: 'confirm', name: 'ok', message, default: true }]);
  return Boolean(ok);
}

export async function runAgent(opts: AgentOpts = {}, deps: AgentDeps = {}): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const cwd = deps.cwd ?? process.cwd();
  const agentsPath = join(cwd, 'AGENTS.md');
  const claudePath = join(cwd, 'CLAUDE.md');

  // --- Plan (no writes) ---
  const plan: PlanItem[] = [];

  const agentsExisting = readOrNull(agentsPath);
  let agentsMerge;
  try {
    agentsMerge = mergeManagedBlock(agentsExisting, AGENT_BOOTSTRAP_BLOCK);
  } catch (e) {
    // Rethrow as a CloudGridError that names the file so it routes through the
    // top-level handler (telemetry + clear message) and the CLAUDE.md write is
    // never reached. Keep the core CorruptedBlockError IO/CLI-free.
    if (e instanceof CorruptedBlockError) {
      throw new CloudGridError(
        `AGENTS.md has a corrupted CloudGrid block. Fix or remove the ` +
          `${CLOUDGRID_BLOCK_START} / ${CLOUDGRID_BLOCK_END} markers and re-run.`,
      );
    }
    throw e;
  }
  plan.push({
    label: 'AGENTS.md',
    path: agentsPath,
    status: agentsMerge.status,
    content: agentsMerge.content,
    warn:
      agentsExisting && countBlockStarts(agentsExisting) > 1
        ? 'multiple CloudGrid blocks found; only the first was refreshed'
        : undefined,
  });

  if (isSymlinkTo(claudePath, agentsPath)) {
    plan.push({
      label: 'CLAUDE.md',
      path: claudePath,
      status: 'unchanged',
      content: '',
      note: 'symlink to AGENTS.md',
    });
  } else {
    const claudeMerge = ensureClaudeImport(readOrNull(claudePath));
    plan.push({ label: 'CLAUDE.md', path: claudePath, status: claudeMerge.status, content: claudeMerge.content });
  }

  const changing = plan.filter((p) => p.status !== 'unchanged');

  // --- No-op: report + exit, never prompt ---
  if (changing.length === 0) {
    out.json(() => planPayload(plan, false));
    out.human(() => {
      for (const p of plan) report(p);
      process.stdout.write('Already set up.\n');
      return undefined;
    });
    return;
  }

  // --- Dry run: print plan, write nothing ---
  if (opts.dryRun) {
    out.json(() => planPayload(plan, false));
    out.human(() => {
      for (const p of plan) process.stdout.write(`[dry-run] ${line(p)}\n`);
      return undefined;
    });
    return;
  }

  // --- Consent ---
  if (!opts.yes) {
    // Structured mode is non-interactive by contract — a script can't answer a
    // prompt. Fail fast telling the caller to pass --yes rather than silently
    // blocking on (or, worse, auto-answering) a prompt.
    if (out.isStructured) {
      // Same envelope contract as unplug's structured-mode guard (#1785 review):
      // machine consumers get a parseable {error:{code}} on stdout, not a bare
      // stderr line.
      throw new V2ApiError(
        400,
        'CONFIRMATION_REQUIRED',
        'Writing agent files needs confirmation. Re-run with --yes for machine-readable output: grid agent --yes -o json',
      );
    }
    const isTTY = deps.isTTY ?? Boolean(process.stdin.isTTY);
    if (!isTTY) {
      throw new CloudGridError(
        'This directory needs confirmation to write agent files. ' +
          'Re-run in a terminal, or add --yes: grid agent --yes',
      );
    }
    for (const p of changing) {
      process.stdout.write(`${line(p)}\n`);
      if (p.warn) process.stdout.write(`warning: ${p.warn}\n`);
    }
    const ok = await (deps.confirm ?? defaultConfirm)('Add CloudGrid agent instructions to this directory?');
    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  // --- Apply ---
  for (const p of plan) {
    if (p.status !== 'unchanged') writeFileSync(p.path, p.content, 'utf8');
  }
  out.json(() => planPayload(plan, true));
  out.human(() => {
    for (const p of plan) report(p);
    process.stdout.write('Agents in this directory will now reach for CloudGrid.\n');
    return undefined;
  });
}

/**
 * Structured payload for `agent`: the per-file plan (label + status) plus
 * whether the writes were applied. `dry-run` and the no-op path report
 * `applied: false`; the apply path reports `true`.
 */
function planPayload(plan: PlanItem[], applied: boolean): Record<string, unknown> {
  return {
    files: plan.map((p) => ({
      label: p.label,
      status: p.status,
      ...(detail(p) ? { detail: detail(p) } : {}),
      ...(p.warn ? { warning: p.warn } : {}),
    })),
    applied,
  };
}

function detail(p: PlanItem): string {
  if (p.note) return `(${p.note})`;
  if (p.label === 'AGENTS.md' && p.status === 'append') return '(appended CloudGrid block)';
  if (p.label === 'AGENTS.md' && p.status === 'replace') return '(refreshed CloudGrid block)';
  if (p.label === 'CLAUDE.md' && p.status === 'append') return '(added @AGENTS.md import)';
  return '';
}

function line(p: PlanItem): string {
  const d = detail(p);
  return `${p.status.padEnd(10)}${p.label}${d ? '  ' + d : ''}`;
}

function report(p: PlanItem): void {
  process.stdout.write(`${line(p)}\n`);
  if (p.warn) process.stdout.write(`warning: ${p.warn}\n`);
}

export const agentCommand = addOutputOption(
  new Command('agent')
    .description('Write CloudGrid agent instructions (AGENTS.md + CLAUDE.md) into this directory')
    .option('-y, --yes', 'Skip the confirmation prompt')
    .option('--dry-run', 'Show what would change without writing')
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (opts: AgentOpts) => {
  // CloudGridError still bubbles to the top-level handler in index.ts (it
  // prints "Error: <message>", fires telemetry, exits 1) — matching siblings.
  // V2ApiError (the structured-mode confirmation guard) routes through
  // handleV2Error so machine consumers get the {error:{code}} envelope on
  // stdout (#1785 review).
  try {
    await runAgent({ yes: opts.yes, dryRun: opts.dryRun, json: opts.json, output: opts.output });
  } catch (err) {
    if (err instanceof V2ApiError) {
      await handleV2Error(err, { json: isStructuredOutput(opts) });
      return;
    }
    throw err;
  }
});
